// PRD.284 — Clinic-wide print branding (Letterhead Classic template)
//
// Print branding shared by Invoice Print and Prescription Print is stored under
// `app_config.extra_json.print_branding`, mirroring the established
// `regional_settings` / `clinic_classification` / `main_modules` patterns. It
// rides the existing PowerSync + SyncWrite `extra_json` path, so no
// schema/migration or codegen is required.
//
// This module is intentionally self-contained (no PowerSync / query-spec
// imports) so its pure logic can be unit-tested in isolation.

/**
 * Clinic-wide print branding. `printFontColor` applies to printed text and
 * app-generated chrome on the Letterhead Classic template; the remaining
 * free-text fields feed the two-column letterhead header/footer. Empty fields
 * are omitted by the renderer.
 */
export interface PrintBrandingConfig {
  /** Hex color (#rrggbb) applied to printed text + template chrome. */
  printFontColor: string;
  /**
   * Prominent left-column name on the Letterhead Classic header. When set it
   * overrides the invoice's attending provider / prescription doctor, so a
   * solo practice can show a fixed name (e.g. "Dr. M. Shanmugam").
   */
  letterheadDoctorName: string;
  doctorQualifications: string;
  doctorSpecialty: string;
  doctorDesignation: string;
  registrationNumber: string;
  additionalRegistrations: string;
  insurancePanel: string;
  /** Extra centered footer lines (newline-separated free text). */
  footerExtraText: string;
}

/**
 * Renderer-facing letterhead content assembled from `print_branding` plus the
 * clinic's local-language name (`org_localname`). Kept separate from the saved
 * per-surface print settings because these are clinic-wide branding, resolved
 * once at each print/preview mount.
 */
export interface PrintLetterheadInfo {
  /** Fixed prominent name for the letterhead header (overrides provider). */
  doctorName?: string;
  qualifications?: string;
  specialty?: string;
  designation?: string;
  registrationNumber?: string;
  additionalRegistrations?: string;
  insurancePanel?: string;
  clinicLocalName?: string;
  footerExtraText?: string;
}

/** Minimal structural shape of the app_config row this module reads. */
export interface PrintBrandingSource {
  extra_json?: string | null;
  org_localname?: string | null;
}

export const DEFAULT_PRINT_FONT_COLOR = '#000000';

export const PRINT_BRANDING_DEFAULTS: PrintBrandingConfig = {
  printFontColor: DEFAULT_PRINT_FONT_COLOR,
  letterheadDoctorName: '',
  doctorQualifications: '',
  doctorSpecialty: '',
  doctorDesignation: '',
  registrationNumber: '',
  additionalRegistrations: '',
  insurancePanel: '',
  footerExtraText: '',
};

const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;

/** Normalize an arbitrary value to a valid 6-digit hex color, or the fallback. */
export function normalizePrintFontColor(
  value: unknown,
  fallback = DEFAULT_PRINT_FONT_COLOR,
): string {
  if (typeof value !== 'string') return fallback;
  const trimmed = value.trim();
  return HEX_COLOR_RE.test(trimmed) ? trimmed.toLowerCase() : fallback;
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function parseExtraJson(extraJson: unknown): Record<string, unknown> {
  if (extraJson == null) return {};
  if (isObject(extraJson)) return extraJson;
  if (typeof extraJson === 'string' && extraJson.trim().length > 0) {
    try {
      const parsed = JSON.parse(extraJson);
      if (isObject(parsed)) return parsed;
    } catch {
      /* fall through */
    }
  }
  return {};
}

function asStr(v: unknown, fallback: string): string {
  return typeof v === 'string' ? v : fallback;
}

/**
 * Extract `print_branding` from an app_config row, applying defaults for any
 * missing or malformed fields. Always returns a fully-populated object so
 * renderers/UI can consume it without null checks.
 */
export function getPrintBranding(row: PrintBrandingSource | null | undefined): PrintBrandingConfig {
  const extra = parseExtraJson(row?.extra_json);
  const raw = extra.print_branding;
  if (!isObject(raw)) return { ...PRINT_BRANDING_DEFAULTS };
  return {
    printFontColor: normalizePrintFontColor(raw.printFontColor),
    letterheadDoctorName: asStr(raw.letterheadDoctorName, ''),
    doctorQualifications: asStr(raw.doctorQualifications, ''),
    doctorSpecialty: asStr(raw.doctorSpecialty, ''),
    doctorDesignation: asStr(raw.doctorDesignation, ''),
    registrationNumber: asStr(raw.registrationNumber, ''),
    additionalRegistrations: asStr(raw.additionalRegistrations, ''),
    insurancePanel: asStr(raw.insurancePanel, ''),
    footerExtraText: asStr(raw.footerExtraText, ''),
  };
}

/**
 * Serialize `print_branding` into an updated `extra_json` JSON string,
 * preserving every other top-level key already present.
 */
export function writePrintBranding(currentExtraJson: unknown, config: PrintBrandingConfig): string {
  const next = { ...parseExtraJson(currentExtraJson) };
  next.print_branding = {
    ...config,
    printFontColor: normalizePrintFontColor(config.printFontColor),
  };
  return JSON.stringify(next);
}

/**
 * Map a `PrintBrandingConfig` (+ the clinic's local-language name) into the
 * renderer-facing letterhead content. Shared by `resolvePrintLetterhead` and by
 * the print wizards, which edit a live draft config and need the preview to
 * reflect it before it is saved.
 */
export function letterheadFromBranding(
  pb: PrintBrandingConfig,
  clinicLocalName?: string | null,
): PrintLetterheadInfo {
  return {
    doctorName: pb.letterheadDoctorName,
    qualifications: pb.doctorQualifications,
    specialty: pb.doctorSpecialty,
    designation: pb.doctorDesignation,
    registrationNumber: pb.registrationNumber,
    additionalRegistrations: pb.additionalRegistrations,
    insurancePanel: pb.insurancePanel,
    clinicLocalName: clinicLocalName ?? '',
    footerExtraText: pb.footerExtraText,
  };
}

/**
 * Resolve the effective print font color + letterhead content for a print/
 * preview mount from the app_config row. Every mount uses this so the wizard
 * preview and the live print stay in sync.
 */
export function resolvePrintLetterhead(row: PrintBrandingSource | null | undefined): {
  fontColor: string;
  letterhead: PrintLetterheadInfo;
} {
  const pb = getPrintBranding(row);
  return {
    fontColor: pb.printFontColor,
    letterhead: letterheadFromBranding(pb, row?.org_localname),
  };
}
